/**
 * Onboarding Wizard — 4-Step Employee Onboarding Flow
 *
 * Steps:
 * 1. Persoenliche Daten (Name, SVNR, IBAN, Adresse, Foto, Notfallkontakt)
 * 2. Anstellung (Eintritt, Anstellungsart, Stunden, Standort, Kosten)
 * 3. System-Einrichtung (App-Zugang, Ausweis, Urlaubskonto)
 * 4. Checkliste (10 toggleable items)
 *
 * @page /mitarbeiter/onboarding
 * @roles admin, ops_manager
 * @api POST /api/mitarbeiter/onboarding
 * @api GET /api/mitarbeiter/:id/onboarding
 * @api PUT /api/mitarbeiter/:id/onboarding/:schritt
 * @api POST /api/mitarbeiter/:id/onboarding/user
 * @api POST /api/mitarbeiter/:id/onboarding/ausweis
 * @api POST /api/mitarbeiter/:id/onboarding/urlaubskonto
 * @api POST /api/mitarbeiter/:id/foto
 * @i18n onboarding.*
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Card, Button } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

function api(path, opts) {
  return fetch(path, { credentials: 'include', ...opts }).then(r => r.json());
}

// ─── Step indicator ──────────────────────────────────────────────
function Stepper({ steps, current, t }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {steps.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <div key={s} className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors ${
              done ? 'bg-[#338edc] border-[#338edc] text-white' :
              active ? 'border-[#338edc] text-[#338edc] bg-white' :
              'border-[var(--line-card,#E5E7EB)] text-[var(--text-tertiary,#6B7280)] bg-white'
            }`}>
              {done ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : i + 1}
            </div>
            <span className={`text-sm hidden sm:inline ${active ? 'font-semibold text-[var(--text-primary,#1A1A2E)]' : 'text-[var(--text-tertiary,#6B7280)]'}`}>
              {t(`onboarding.step${i + 1}`)}
            </span>
            {i < steps.length - 1 && (
              <div className={`w-8 h-px ${done ? 'bg-[#338edc]' : 'bg-[var(--line-card,#E5E7EB)]'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Form Field ──────────────────────────────────────────────────
function Field({ label, required, children, className = '' }) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-[var(--text-secondary,#4B5563)] mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls = 'w-full border border-[var(--line-card,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-white text-[var(--text-primary,#1A1A2E)] focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc] outline-none';
const selectCls = inputCls;

// ─── Step 1: Persoenliche Daten ──────────────────────────────────
function StepPersonal({ form, setForm, fotoFile, setFotoFile, t }) {
  const fotoPreview = fotoFile ? URL.createObjectURL(fotoFile) : null;

  return (
    <Card>
      <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)] mb-5">{t('onboarding.personalData')}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label={t('onboarding.firstName')} required>
          <input className={inputCls} value={form.vorname} onChange={e => setForm(f => ({ ...f, vorname: e.target.value }))} />
        </Field>
        <Field label={t('onboarding.lastName')} required>
          <input className={inputCls} value={form.nachname} onChange={e => setForm(f => ({ ...f, nachname: e.target.value }))} />
        </Field>
        <Field label={t('onboarding.svnr')}>
          <input className={inputCls} value={form.svnr} onChange={e => setForm(f => ({ ...f, svnr: e.target.value }))} placeholder="1234-567890" />
        </Field>
        <Field label={t('onboarding.iban')}>
          <input className={inputCls} value={form.iban} onChange={e => setForm(f => ({ ...f, iban: e.target.value }))} placeholder="AT..." />
        </Field>
        <Field label={t('onboarding.street')}>
          <input className={inputCls} value={form.strasse} onChange={e => setForm(f => ({ ...f, strasse: e.target.value }))} />
        </Field>
        <div className="grid grid-cols-3 gap-2">
          <Field label={t('onboarding.zip')}>
            <input className={inputCls} value={form.plz} onChange={e => setForm(f => ({ ...f, plz: e.target.value }))} />
          </Field>
          <Field label={t('onboarding.city')} className="col-span-2">
            <input className={inputCls} value={form.ort} onChange={e => setForm(f => ({ ...f, ort: e.target.value }))} />
          </Field>
        </div>
        <Field label={t('onboarding.country')}>
          <select className={selectCls} value={form.land} onChange={e => setForm(f => ({ ...f, land: e.target.value }))}>
            <option value="AT">Österreich</option>
            <option value="DE">Deutschland</option>
            <option value="HU">Ungarn</option>
            <option value="SK">Slowakei</option>
            <option value="CZ">Tschechien</option>
          </select>
        </Field>
        <Field label={t('onboarding.startDate')} required>
          <input type="date" className={inputCls} value={form.eintritt_datum} onChange={e => setForm(f => ({ ...f, eintritt_datum: e.target.value }))} />
        </Field>
        <Field label={t('onboarding.photo')}>
          <div className="flex items-center gap-3">
            {fotoPreview && <img src={fotoPreview} alt="" className="w-12 h-14 rounded object-cover border" />}
            <input type="file" accept="image/jpeg,image/png,image/webp"
              className="text-xs text-[var(--text-tertiary,#6B7280)]"
              onChange={e => setFotoFile(e.target.files[0] || null)} />
          </div>
        </Field>
      </div>

      <h4 className="text-xs font-semibold text-[var(--text-secondary,#4B5563)] mt-6 mb-3">{t('onboarding.emergencyContact')}</h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label={t('onboarding.ecName')}>
          <input className={inputCls} value={form.notfallkontakt_name} onChange={e => setForm(f => ({ ...f, notfallkontakt_name: e.target.value }))} />
        </Field>
        <Field label={t('onboarding.ecPhone')}>
          <input className={inputCls} value={form.notfallkontakt_telefon} onChange={e => setForm(f => ({ ...f, notfallkontakt_telefon: e.target.value }))} />
        </Field>
        <Field label={t('onboarding.ecRelation')}>
          <select className={selectCls} value={form.notfallkontakt_beziehung} onChange={e => setForm(f => ({ ...f, notfallkontakt_beziehung: e.target.value }))}>
            <option value="">—</option>
            <option value="partner">{t('onboarding.relPartner')}</option>
            <option value="elternteil">{t('onboarding.relParent')}</option>
            <option value="kind">{t('onboarding.relChild')}</option>
            <option value="geschwister">{t('onboarding.relSibling')}</option>
            <option value="sonstige">{t('onboarding.relOther')}</option>
          </select>
        </Field>
      </div>
    </Card>
  );
}

// ─── Step 2: Anstellung ──────────────────────────────────────────
function StepEmployment({ form, setForm, standorte, t }) {
  return (
    <Card>
      <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)] mb-5">{t('onboarding.employment')}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label={t('onboarding.startDate')} required>
          <input type="date" className={inputCls} value={form.eintritt_datum} onChange={e => setForm(f => ({ ...f, eintritt_datum: e.target.value }))} />
        </Field>
        <Field label={t('onboarding.employmentType')}>
          <select className={selectCls} value={form.anstellungsart} onChange={e => setForm(f => ({ ...f, anstellungsart: e.target.value }))}>
            <option value="angestellter">{t('employees.detail.stammdaten_angestellter')}</option>
            <option value="arbeiter">{t('employees.detail.stammdaten_arbeiter')}</option>
            <option value="lehrling">{t('employees.detail.stammdaten_lehrling')}</option>
            <option value="aushilfe">{t('employees.detail.stammdaten_aushilfe')}</option>
            <option value="geringfuegig">{t('employees.detail.stammdaten_geringfuegig')}</option>
          </select>
        </Field>
        <Field label={t('onboarding.weeklyHours')} required>
          <input type="number" className={inputCls} value={form.wochenstunden_soll} onChange={e => setForm(f => ({ ...f, wochenstunden_soll: e.target.value }))} min="0" max="40" step="0.5" />
        </Field>
        <Field label={t('onboarding.workdays')}>
          <input className={inputCls} value={form.arbeitstage_muster} onChange={e => setForm(f => ({ ...f, arbeitstage_muster: e.target.value }))} placeholder="Mo-Fr" />
        </Field>
        <Field label={t('onboarding.costCenter')}>
          <input className={inputCls} value={form.kostenstelle} onChange={e => setForm(f => ({ ...f, kostenstelle: e.target.value }))} />
        </Field>
        <Field label={t('onboarding.location')}>
          <select className={selectCls} value={form.standort_id} onChange={e => setForm(f => ({ ...f, standort_id: e.target.value }))}>
            <option value="">—</option>
            {standorte.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
        <Field label={t('onboarding.grossSalary')}>
          <input type="number" className={inputCls} value={form.brutto_monatlich} onChange={e => setForm(f => ({ ...f, brutto_monatlich: e.target.value }))} min="0" step="10" />
        </Field>
        <Field label={t('onboarding.warehouseRole')}>
          <select className={selectCls} value={form.lager_rolle} onChange={e => setForm(f => ({ ...f, lager_rolle: e.target.value }))}>
            <option value="pick_pack">Pick & Pack</option>
            <option value="picker">Picker</option>
            <option value="packer">Packer</option>
          </select>
        </Field>
      </div>
    </Card>
  );
}

// ─── Step 3: System Setup ────────────────────────────────────────
function StepSystem({ maId, onboardingData, reload, t }) {
  const [email, setEmail] = useState('');
  const [rolle, setRolle] = useState('viewer');
  const [tempPw, setTempPw] = useState(null);
  const [ausweisPreview, setAusweisPreview] = useState(null);
  const [urlaubInfo, setUrlaubInfo] = useState(null);
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});

  const doAction = async (key, fn) => {
    setLoading(l => ({ ...l, [key]: true }));
    setErrors(e => ({ ...e, [key]: null }));
    try {
      const res = await fn();
      if (res.error) setErrors(e => ({ ...e, [key]: res.error || res.message }));
      return res;
    } catch (err) {
      setErrors(e => ({ ...e, [key]: err.message }));
    } finally {
      setLoading(l => ({ ...l, [key]: false }));
      reload();
    }
  };

  const createUser = () => doAction('user', async () => {
    const res = await api(`/api/mitarbeiter/${maId}/onboarding/user`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, rolle }),
    });
    if (res.ok) setTempPw(res.temp_password);
    return res;
  });

  const generateAusweis = () => doAction('ausweis', async () => {
    const res = await api(`/api/mitarbeiter/${maId}/onboarding/ausweis`, { method: 'POST' });
    if (res.ok) setAusweisPreview(res.preview);
    return res;
  });

  const createUrlaub = () => doAction('urlaub', async () => {
    const res = await api(`/api/mitarbeiter/${maId}/onboarding/urlaubskonto`, { method: 'POST' });
    if (res.ok || res.already_exists) setUrlaubInfo(res.berechnung);
    return res;
  });

  const hasUser = onboardingData?.has_user || tempPw;
  const hasAusweis = onboardingData?.has_ausweis || ausweisPreview;
  const hasUrlaub = onboardingData?.has_urlaubskonto || urlaubInfo;

  return (
    <div className="space-y-4">
      {/* App-Zugang */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)]">{t('onboarding.appAccess')}</h3>
          {hasUser && <span className="text-green-600 text-sm font-medium flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
            {t('onboarding.done')}
          </span>}
        </div>
        {!hasUser ? (
          <div className="flex gap-3 items-end">
            <Field label={t('onboarding.email')} className="flex-1">
              <input type="email" className={inputCls} value={email} onChange={e => setEmail(e.target.value)} placeholder="name@weship.eu" />
            </Field>
            <Field label={t('onboarding.role')}>
              <select className={selectCls} value={rolle} onChange={e => setRolle(e.target.value)}>
                <option value="viewer">Viewer</option>
                <option value="teamleiter">Teamleiter</option>
                <option value="ops_manager">Ops Manager</option>
                <option value="administrator">Administrator</option>
              </select>
            </Field>
            <Button onClick={createUser} loading={loading.user} size="md">{t('onboarding.create')}</Button>
          </div>
        ) : tempPw ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
            <p className="text-green-800 font-medium">{t('onboarding.userCreated')}</p>
            <p className="text-green-700 mt-1">{t('onboarding.tempPassword')}: <code className="font-mono bg-green-100 px-1.5 py-0.5 rounded">{tempPw}</code></p>
          </div>
        ) : (
          <p className="text-sm text-[var(--text-tertiary,#6B7280)]">{t('onboarding.userExists')}</p>
        )}
        {errors.user && <p className="text-red-500 text-xs mt-2">{errors.user}</p>}
      </Card>

      {/* Ausweis */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)]">{t('onboarding.idCard')}</h3>
          {hasAusweis && <span className="text-green-600 text-sm font-medium flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
            {t('onboarding.done')}
          </span>}
        </div>
        {!hasAusweis ? (
          <Button onClick={generateAusweis} loading={loading.ausweis} size="md">{t('onboarding.generate')}</Button>
        ) : ausweisPreview ? (
          <div>
            <img src={ausweisPreview} alt="Ausweis" className="max-w-xs rounded border" />
          </div>
        ) : (
          <p className="text-sm text-[var(--text-tertiary,#6B7280)]">{t('onboarding.idCardExists')}</p>
        )}
        {errors.ausweis && <p className="text-red-500 text-xs mt-2">{errors.ausweis}</p>}
      </Card>

      {/* Urlaubskonto */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)]">{t('onboarding.vacationAccount')}</h3>
          {hasUrlaub && <span className="text-green-600 text-sm font-medium flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
            {t('onboarding.done')}
          </span>}
        </div>
        {urlaubInfo && (
          <div className="grid grid-cols-3 gap-3 mb-3 text-sm">
            <div><span className="text-[var(--text-tertiary,#6B7280)]">{t('onboarding.entitlement')}:</span> <strong>{urlaubInfo.anspruch}d</strong></div>
            <div><span className="text-[var(--text-tertiary,#6B7280)]">{t('onboarding.accrued')}:</span> <strong>{urlaubInfo.aufgebaut}d</strong></div>
            <div><span className="text-[var(--text-tertiary,#6B7280)]">{t('onboarding.waitingPeriod')}:</span> <strong>{urlaubInfo.in_wartefrist ? t('onboarding.yes') : t('onboarding.no')}</strong></div>
          </div>
        )}
        {!hasUrlaub && (
          <Button onClick={createUrlaub} loading={loading.urlaub} size="md">{t('onboarding.createAccount')}</Button>
        )}
        {errors.urlaub && <p className="text-red-500 text-xs mt-2">{errors.urlaub}</p>}
      </Card>
    </div>
  );
}

// ─── Step 4: Checkliste ──────────────────────────────────────────
const SCHRITT_LABELS = {
  stammdaten: 'onboarding.clStammdaten',
  arbeitsvertrag: 'onboarding.clArbeitsvertrag',
  app_zugang: 'onboarding.clAppZugang',
  ausweis: 'onboarding.clAusweis',
  urlaubskonto: 'onboarding.clUrlaubskonto',
  schluessel: 'onboarding.clSchluessel',
  arbeitskleidung: 'onboarding.clArbeitskleidung',
  scanner_erhalten: 'onboarding.clScanner',
  einschulung_geplant: 'onboarding.clEinschulung',
  steuerberater_informiert: 'onboarding.clSteuerberater',
};

function StepChecklist({ maId, checkliste, reload, t }) {
  const [toggling, setToggling] = useState(null);
  const done = checkliste.filter(c => c.erledigt).length;
  const total = checkliste.length;
  const pct = total ? Math.round((done / total) * 100) : 0;

  const toggle = async (schritt) => {
    setToggling(schritt);
    await api(`/api/mitarbeiter/${maId}/onboarding/${schritt}`, { method: 'PUT' });
    await reload();
    setToggling(null);
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)]">{t('onboarding.checklist')}</h3>
        <span className="text-sm text-[var(--text-tertiary,#6B7280)]">{done}/{total} ({pct}%)</span>
      </div>
      {/* Progress bar */}
      <div className="w-full h-2 bg-[var(--bg-inset,#F8F9FC)] rounded-full mb-5">
        <div className="h-2 rounded-full bg-[#338edc] transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="space-y-2">
        {checkliste.map(item => (
          <button key={item.schritt}
            className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
              item.erledigt ? 'bg-green-50 border-green-200' : 'border-[var(--line-card,#E5E7EB)] hover:bg-[var(--bg-inset,#F8F9FC)]'
            }`}
            onClick={() => toggle(item.schritt)}
            disabled={toggling === item.schritt}
          >
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
              item.erledigt ? 'bg-green-500 border-green-500' : 'border-[var(--line-card,#E5E7EB)]'
            }`}>
              {item.erledigt && (
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <span className={`text-sm ${item.erledigt ? 'text-green-800 line-through' : 'text-[var(--text-primary,#1A1A2E)]'}`}>
              {t(SCHRITT_LABELS[item.schritt] || item.schritt)}
            </span>
            {toggling === item.schritt && (
              <svg className="animate-spin w-4 h-4 text-[var(--text-tertiary,#6B7280)] ml-auto" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="3" strokeDasharray="60 30" />
              </svg>
            )}
          </button>
        ))}
      </div>
    </Card>
  );
}

// ─── Main Page ───────────────────────────────────────────────────
export default function OnboardingPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [maId, setMaId] = useState(null);
  const [onboardingData, setOnboardingData] = useState(null);
  const [standorte, setStandorte] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [fotoFile, setFotoFile] = useState(null);

  const [form, setForm] = useState({
    vorname: '', nachname: '', svnr: '', iban: '',
    strasse: '', plz: '', ort: '', land: 'AT',
    notfallkontakt_name: '', notfallkontakt_telefon: '', notfallkontakt_beziehung: '',
    eintritt_datum: new Date().toISOString().slice(0, 10),
    anstellungsart: 'angestellter', wochenstunden_soll: '38.5',
    arbeitstage_muster: 'Mo-Fr', kostenstelle: '', standort_id: '',
    brutto_monatlich: '', lager_rolle: 'pick_pack',
  });

  // Load standorte
  useEffect(() => {
    api('/api/standorte').then(r => { if (Array.isArray(r)) setStandorte(r); });
  }, []);

  // Load onboarding data when maId is set
  const loadOnboarding = useCallback(async () => {
    if (!maId) return;
    const data = await api(`/api/mitarbeiter/${maId}/onboarding`);
    if (!data.error) setOnboardingData(data);
  }, [maId]);

  useEffect(() => { loadOnboarding(); }, [loadOnboarding]);

  // Determine starting step from existing data
  useEffect(() => {
    if (!onboardingData) return;
    const cl = onboardingData.checkliste || [];
    const stammdatenDone = cl.find(c => c.schritt === 'stammdaten')?.erledigt;
    if (stammdatenDone && step < 2) setStep(2);
  }, [onboardingData]);

  const steps = ['personal', 'employment', 'system', 'checklist'];

  // Step 1: Submit personal data → create MA
  const submitStep1 = async () => {
    if (!form.vorname || !form.nachname) { setError(t('onboarding.nameRequired')); return; }
    if (!form.eintritt_datum) { setError(t('onboarding.startDateRequired')); return; }
    setSaving(true);
    setError(null);
    const res = await api('/api/mitarbeiter/onboarding', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    if (res.error) { setError(res.error || res.message); setSaving(false); return; }
    setMaId(res.mitarbeiter.id);

    // Upload foto if selected
    if (fotoFile && res.mitarbeiter.id) {
      const fd = new FormData();
      fd.append('file', fotoFile);
      await fetch(`/api/mitarbeiter/${res.mitarbeiter.id}/foto`, { method: 'POST', credentials: 'include', body: fd });
    }

    setSaving(false);
    setStep(1);
  };

  // Step 2: Submit employment data → update MA
  const submitStep2 = async () => {
    setSaving(true);
    setError(null);
    const payload = {};
    for (const key of ['eintritt_datum', 'anstellungsart', 'wochenstunden_soll', 'arbeitstage_muster', 'kostenstelle', 'standort_id', 'brutto_monatlich']) {
      if (form[key]) payload[key] = form[key];
    }
    // lager_rolle goes to main mitarbeiter update
    if (form.lager_rolle) payload.lager_rolle = form.lager_rolle;

    const res = await api(`/api/mitarbeiter/${maId}/stammdaten`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.error) { setError(res.error || res.message); setSaving(false); return; }
    setSaving(false);
    setStep(2);
    loadOnboarding();
  };

  const finish = () => {
    navigate(`/mitarbeiter/${maId}`);
  };

  return (
    <div className="p-page space-y-6">
      <PageHeader title={t('onboarding.title')} subtitle={t('onboarding.subtitle')} />

      <Stepper steps={steps} current={step} t={t} />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
      )}

      {step === 0 && (
        <>
          <StepPersonal form={form} setForm={setForm} fotoFile={fotoFile} setFotoFile={setFotoFile} t={t} />
          <div className="flex justify-end">
            <Button onClick={submitStep1} loading={saving}>{t('onboarding.next')}</Button>
          </div>
        </>
      )}

      {step === 1 && (
        <>
          <StepEmployment form={form} setForm={setForm} standorte={standorte} t={t} />
          <div className="flex justify-between">
            <Button variant="secondary" onClick={() => setStep(0)}>{t('onboarding.back')}</Button>
            <Button onClick={submitStep2} loading={saving}>{t('onboarding.next')}</Button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <StepSystem maId={maId} onboardingData={onboardingData} reload={loadOnboarding} t={t} />
          <div className="flex justify-between">
            <Button variant="secondary" onClick={() => setStep(1)}>{t('onboarding.back')}</Button>
            <Button onClick={() => setStep(3)}>{t('onboarding.next')}</Button>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <StepChecklist maId={maId} checkliste={onboardingData?.checkliste || []} reload={loadOnboarding} t={t} />
          <div className="flex justify-between">
            <Button variant="secondary" onClick={() => setStep(2)}>{t('onboarding.back')}</Button>
            <Button onClick={finish}>{t('onboarding.finish')}</Button>
          </div>
        </>
      )}
    </div>
  );
}
